import React, { Component } from "react";
import { observer } from "mobx-react";
import { Button, Card, Grid, Icon, List, Label, Segment, Modal, Form, Input, Image } from "semantic-ui-react";
import PropTypes from "prop-types";
import Immersive3dTourAppPageStore from "~/stores/Immersive3dTourAppPageStore";
import Immersive3dTourPropertyType from "~/constants/Immersive3dTour/Immersive3dTourPropertyType";
import FlashMessageStore from "~/stores/FlashMessageStore";
import FlashMessage from "~/components/FlashMessage";
import ContentLoader from "~/components/element/ContentLoader";
import ProductId from "~/constants/ProductId";
import "../styles/CreatePropertyType.less";

@observer
class CreatePropertyType extends Component {
  flashMessageStore = null;

  constructor(props) {
    super(props);

    this.flashMessageStore = new FlashMessageStore();

    this.state = {
      isLoading: false,
      showInitialFloorsPrompt: false,
    };
  }

  selectPropertyType(propertyType) {
    // If residential, the user needs to select the number of floors
    if (propertyType === Immersive3dTourPropertyType.RESIDENTIAL) {
      this.setState({ showInitialFloorsPrompt: true });

      return;
    }

    // For commercial, we save the property type directly
    this.savePropertyType(propertyType);
  }

  savePropertyType(propertyType = null) {
    const { immersive3dTourAppPageStore } = this.props;
    const { immersive3dTourStore } = immersive3dTourAppPageStore;
    const { immersive3dTour } = immersive3dTourStore;

    // types, according to Immersive3dTourPropertyType
    if (Object.values(Immersive3dTourPropertyType).indexOf(propertyType) === -1) {
      throw new Error("Error. PropertyType not set.");
    }

    this.setState({ isLoading: true });

    immersive3dTourStore.update(immersive3dTour.tourId, { propertyType }).catch(error => {
      this.flashMessageStore.setMessage(error.message, "negative");
    });
  }

  render() {
    const { isLoading, showInitialFloorsPrompt } = this.state;
    const { immersive3dTourAppPageStore } = this.props;

    const { immersive3dTourTabStore, productStore } = immersive3dTourAppPageStore;
    const { productDiscountedCreditCosts } = productStore;

    const { propertyFloorsCount } = immersive3dTourTabStore;

    if (isLoading) {
      return <ContentLoader />;
    }

    return (
      <>
        <FlashMessage flashMessageStore={this.flashMessageStore} />
        <Modal
          open={showInitialFloorsPrompt}
          onClose={() => this.setState({ showInitialFloorsPrompt: false })}
          size="mini"
        >
          <Modal.Header>Property Floors</Modal.Header>
          <Modal.Content>
            <Modal.Description>
              <Form>
                <Form.Field
                  control={Input}
                  label={`How many floors were photographed for this property? ${propertyFloorsCount}`}
                  min={1}
                  max={10}
                  onChange={e => immersive3dTourTabStore.setPropertyFloorsCount(parseInt(e.target.value, 10))}
                  step={1}
                  type="range"
                  value={propertyFloorsCount}
                />
              </Form>
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button primary onClick={() => this.savePropertyType(Immersive3dTourPropertyType.RESIDENTIAL)}>
              Continue
            </Button>
            <Button secondary onClick={() => this.setState({ showInitialFloorsPrompt: false })}>
              Cancel
            </Button>
          </Modal.Actions>
        </Modal>
        <Grid className="selectPropertyType">
          <Grid.Row centered>
            <Grid.Column mobile={16} tablet={12} computer={8}>
              <Card.Group itemsPerRow={1}>
                <Card fluid>
                  <Card.Content>
                    <Label ribbon>New pricing</Label>
                    <Card.Header textAlign="center">Immersive 3D Tours</Card.Header>
                  </Card.Content>
                  <Card.Content extra>
                    <div className="equalWrapper">
                      <div className="equalColumn">
                        <Image src="/www/img/basic-floor-plan-example.svg" fluid />
                        <p>Basic Floor Plan</p>
                      </div>
                      <div className="equalColumn">
                        <Image src="/www/img/enhanced-floor-plan-example.svg" fluid />
                        <p>Enhanced Floor Plan</p>
                      </div>
                    </div>
                  </Card.Content>
                  <Card.Content extra>
                    <Card.Description>
                      <List>
                        <List.Item>
                          <Icon name="exclamation circle" color="black" />
                          <List.Content>Requires Floor Images Grouping</List.Content>
                        </List.Item>
                        <List.Item className="italic">
                          <Icon name="plus" color="black" />
                          <List.Content>
                            Single Property Website for {productDiscountedCreditCosts[ProductId.PUBLISH_TOUR]} credits
                          </List.Content>
                        </List.Item>
                        <List.Item className="italic">
                          <Icon name="plus" color="black" />
                          <List.Content>Enhanced Floor Plan + Dollhouse (price per sq. ft.)</List.Content>
                        </List.Item>
                        <List.Item>
                          <Icon name="check" color="green" />
                          <List.Content>Standalone lifetime license for offline viewing</List.Content>
                        </List.Item>
                        <List.Item>
                          <Icon name="check" color="green" />
                          <List.Content>Basic Floor Plan</List.Content>
                        </List.Item>
                        <List.Item>
                          <Icon name="check" color="green" />
                          <List.Content>Hosting for 12 months</List.Content>
                        </List.Item>
                        <List.Item>
                          <Icon name="check" color="green" />
                          <List.Content>Measurement Tools</List.Content>
                        </List.Item>
                        <List.Item>
                          <Icon name="check" color="green" />
                          <List.Content>UiTags (3D hot spots integration)</List.Content>
                        </List.Item>
                        <List.Item>
                          <Icon name="check" color="green" />
                          <List.Content>HDR and Image Enhancement</List.Content>
                        </List.Item>
                        <List.Item>
                          <Icon name="check" color="green" />
                          <List.Content>
                            3D Pocket Website
                            <span dangerouslySetInnerHTML={{ __html: "&trade;" }} /> Design
                          </List.Content>
                        </List.Item>
                      </List>
                    </Card.Description>
                  </Card.Content>
                  <Card.Content extra textAlign="center">
                    <Button primary onClick={() => this.selectPropertyType(Immersive3dTourPropertyType.RESIDENTIAL)}>
                      Start your Immersive 3D Tour
                    </Button>
                  </Card.Content>
                </Card>
              </Card.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Segment basic textAlign="center">
          New to Immersive 3D Tours?{" "}
          <a href="https://www.tourbuzz.com/shooting_instructions" target="_blank" rel="noreferrer">
            Read our shooting instructions
          </a>{" "}
          or{" "}
          <a href="https://zoom.us/webinar/register/WN_2jAn3JOyQwqZ8pk-HLsLpQ" target="_blank" rel="noreferrer">
            Join our next webinar!
          </a>
        </Segment>
      </>
    );
  }
}

CreatePropertyType.propTypes = {
  immersive3dTourAppPageStore: PropTypes.instanceOf(Immersive3dTourAppPageStore),
};

export default CreatePropertyType;
